// src/services/sanitarySettlementService.js
// =============================================================================
// Frontend API wrapper for Sanitary daily settlements / calculation runs
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitarySettlementService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}


function normalizeListPayload(payload, keys = []) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  for (const key of keys) {
    if (Array.isArray(payload[key])) return payload[key];
  }

  if (Array.isArray(payload.items)) return payload.items;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.data)) return payload.data;

  return [];
}

function isBenignEmptyListError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status !== 404 || !body || typeof body !== "object") return false;

  const code = String(body.code || "").toUpperCase();
  const message = String(body.message || body.error || "").toLowerCase();
  return code.includes("NOT_FOUND") || message.includes("not found") || message.includes("nie znaleziono");
}

function buildParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

export async function fetchSanitaryCalculationRuns(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/settlements/runs", { params: buildParams(params) });
    return normalizeListPayload(res.data, ["runs", "calculationRuns"]);
  } catch (err) {
    if (isBenignEmptyListError(err)) return [];
    throwApiError("fetchSanitaryCalculationRuns", err);
  }
}

export async function fetchSanitaryCalculationRun(runId) {
  try {
    const res = await apiClient.get(`/sanitary/settlements/runs/${runId}`);
    return res.data;
  } catch (err) {
    throwApiError("fetchSanitaryCalculationRun", err);
  }
}

export async function fetchSanitaryConsumptionEntries(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/settlements/entries", { params: buildParams(params) });
    return normalizeListPayload(res.data, ["entries", "consumptionEntries"]);
  } catch (err) {
    if (isBenignEmptyListError(err)) return [];
    throwApiError("fetchSanitaryConsumptionEntries", err);
  }
}


export async function fetchSanitaryConsumptionEntriesPage(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/settlements/entries", {
      params: buildParams({ ...params, paged: true }),
    });
    const payload = res.data && typeof res.data === "object" ? res.data : {};
    return {
      items: normalizeListPayload(payload, ["entries", "consumptionEntries"]),
      pagination: payload.pagination || { page: 1, page_size: Number(params.page_size || 50), total: 0, page_count: 1 },
      summary: payload.summary || {},
      filters: payload.filters || { rules: [], products: [], statuses: [] },
    };
  } catch (err) {
    if (isBenignEmptyListError(err)) {
      return {
        items: [],
        pagination: { page: 1, page_size: Number(params.page_size || 50), total: 0, page_count: 1 },
        summary: {},
        filters: { rules: [], products: [], statuses: [] },
      };
    }
    throwApiError("fetchSanitaryConsumptionEntriesPage", err);
  }
}

export async function calculateSanitaryConsumption(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/settlements/calculate", payload);
    return res.data;
  } catch (err) {
    throwApiError("calculateSanitaryConsumption", err);
  }
}

export async function confirmSanitaryConsumptionEntry(entryId, payload = {}) {
  try {
    const res = await apiClient.post(`/sanitary/settlements/entries/${entryId}/confirm`, payload);
    return res.data;
  } catch (err) {
    throwApiError("confirmSanitaryConsumptionEntry", err);
  }
}

export async function adjustSanitaryConsumptionEntry(entryId, payload = {}) {
  try {
    const res = await apiClient.post(`/sanitary/settlements/entries/${entryId}/adjust`, payload);
    return res.data;
  } catch (err) {
    throwApiError("adjustSanitaryConsumptionEntry", err);
  }
}

export async function rejectSanitaryConsumptionEntry(entryId, payload = {}) {
  try {
    const res = await apiClient.post(`/sanitary/settlements/entries/${entryId}/reject`, payload);
    return res.data;
  } catch (err) {
    throwApiError("rejectSanitaryConsumptionEntry", err);
  }
}

export async function postSanitaryConsumptionEntryToStock(entryId, payload = {}) {
  try {
    const res = await apiClient.post(`/sanitary/settlements/entries/${entryId}/post-to-stock`, payload);
    return res.data;
  } catch (err) {
    throwApiError("postSanitaryConsumptionEntryToStock", err);
  }
}

export async function postConfirmedSanitaryConsumptionEntries(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/settlements/post-confirmed", payload);
    return res.data;
  } catch (err) {
    throwApiError("postConfirmedSanitaryConsumptionEntries", err);
  }
}

export async function historicalCatchUpSanitaryConsumption(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/settlements/historical-catch-up", payload);
    return res.data;
  } catch (err) {
    throwApiError("historicalCatchUpSanitaryConsumption", err);
  }
}

const sanitarySettlementService = {
  fetchSanitaryCalculationRuns,
  fetchSanitaryCalculationRun,
  fetchSanitaryConsumptionEntries,
  fetchSanitaryConsumptionEntriesPage,
  calculateSanitaryConsumption,
  confirmSanitaryConsumptionEntry,
  adjustSanitaryConsumptionEntry,
  rejectSanitaryConsumptionEntry,
  postSanitaryConsumptionEntryToStock,
  postConfirmedSanitaryConsumptionEntries,
  historicalCatchUpSanitaryConsumption,
};

export default sanitarySettlementService;
